import * as dayjs from 'dayjs';
import { MapUtils } from '../../../utility/map-utils.js';
import { getFormattedName, getInitialsFromName } from '../../../utility/general-utility.js';
import { DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../../dto/dto.js';
import { PhotoReference } from '../file-reference/photo-reference.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { ICopyWithBusinessRef } from '../../../contracts/copy-with-business-ref.js';
import { TimeZone } from '../../../time/timezone.js';
import { Review } from '../../review.js';

export class PoiReview extends Review implements ICopyWithBusinessRef {
  static readonly TYPE_ID: string = '85534cc308c04252b7797d9b6f47e8be';

  public readonly reviewerInitials;
  public readonly reviewerFormattedName;

  constructor(
    public readonly uniqueId: string,
    public readonly creationDate: dayjs.Dayjs,
    public readonly businessRef: BusinessModuleRef,
    public readonly reviewText: string,
    public readonly reviewRating: number,
    public readonly reviewerId: string,
    public readonly reviewerFirstName: string,
    public readonly reviewerLastName: string,
    public readonly isAnonymous: boolean,
    public readonly reviewerPhoto: PhotoReference | null,
  ) {
    super();
    this.reviewerInitials = getInitialsFromName(reviewerFirstName, reviewerLastName)!;
    this.reviewerFormattedName = getFormattedName(reviewerFirstName, reviewerLastName)!;
  }

  get $typeId(): string {
    return PoiReview.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): PoiReview {
    return new PoiReview(
        MapUtils.get<string>(map, 'uniqueId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        MapUtils.get<string>(map, 'reviewText'),
        MapUtils.get<number>(map, 'reviewRating'),
        MapUtils.get<string>(map, 'reviewerId'),
        MapUtils.get<string>(map, 'reviewerFirstName'),
        MapUtils.get<string>(map, 'reviewerLastName'),
        MapUtils.get<boolean>(map, 'isAnonymous'),
        context.fromDataOrNull<PhotoReference>(MapUtils.getOrNull<SerializedData>(map, 'reviewerPhoto')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creationDate': context.encodeDate(this.creationDate),
      'businessRef': this.businessRef.encode(),
      'reviewText': this.reviewText,
      'reviewRating': this.reviewRating,
      'reviewerId': this.reviewerId,
      'reviewerFirstName': this.reviewerFirstName,
      'reviewerLastName': this.reviewerLastName,
      'isAnonymous': this.isAnonymous,
      'reviewerPhoto': context.toDataOrNull(this.reviewerPhoto),
      'reviewTextLength': this.reviewText.trim().length,
    };
  }

  clone(): this {
    return new PoiReview(
        this.uniqueId,
        TimeZone.cloneDate(this.creationDate),
        this.businessRef.clone(),
        this.reviewText,
        this.reviewRating,
        this.reviewerId,
        this.reviewerFirstName,
        this.reviewerLastName,
        this.isAnonymous,
        this.reviewerPhoto?.clone() ?? null,
    ) as this;
  }

  isSame(other: PoiReview): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId == this.uniqueId &&
      other.creationDate == this.creationDate &&
      isSameData(other.businessRef, this.businessRef) &&
      other.reviewText == this.reviewText &&
      other.reviewRating == this.reviewRating &&
      other.reviewerId == this.reviewerId &&
      other.reviewerFirstName == this.reviewerFirstName &&
      other.reviewerLastName == this.reviewerLastName &&
      other.isAnonymous == this.isAnonymous &&
      isSameData(other.reviewerPhoto, this.reviewerPhoto);
  }

  copyWithBusinessRef(businessRef: BusinessModuleRef): this {
    return new PoiReview(
        this.uniqueId,
        TimeZone.cloneDate(this.creationDate),
        businessRef.clone(),
        this.reviewText,
        this.reviewRating,
        this.reviewerId,
        this.reviewerFirstName,
        this.reviewerLastName,
        this.isAnonymous,
        this.reviewerPhoto?.clone() ?? null,
    ) as this;
  }
}

export class PoiReviewBuilder extends DataModelBuilder<PoiReview> {
  build(context: SerializationContext, map: SerializedData): PoiReview {
    return PoiReview.fromMap(context, map);
  }
}
